
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Customer } from '../types';
import { useToast } from '@/components/ui/use-toast';

interface UseCreateCustomerFormProps {
  onCustomerCreated: (customerId: string, customerName: string) => void;
  onCancel: () => void;
  editCustomerId?: string | null;
  onClientUpdated?: (client: any) => void;
  initialClient?: Customer | null;
  preselectedShopId?: string;
}

export const useCreateCustomerForm = ({
  onCustomerCreated,
  onCancel,
  editCustomerId = null,
  onClientUpdated,
  initialClient = null,
  preselectedShopId
}: UseCreateCustomerFormProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{name?: string, phone?: string}>({});
  const { toast } = useToast();
  
  // Load customer data if editing
  const loadCustomerData = async () => {
    if (!editCustomerId) return;
    
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', editCustomerId)
        .single();
        
      if (error) throw error;
      
      if (data) {
        setName(data.name || '');
        setEmail(data.email || '');
        setPhone(data.phone || '');
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données du client.',
        variant: 'destructive',
      });
    }
  };
  
  // Initialize with initialClient if provided
  useEffect(() => {
    if (initialClient) {
      setName(initialClient.name || '');
      setEmail(initialClient.email || '');
      setPhone(initialClient.phone || '');
    }
  }, [initialClient]);

  // Save customer
  const handleSave = async () => {
    // Validate
    const newErrors: {name?: string, phone?: string} = {};
    if (!name.trim()) newErrors.name = 'Le nom est requis';
    if (!phone.trim()) newErrors.phone = 'Le téléphone est requis';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Vérifier que l'ID de boutique est présent
      if (!preselectedShopId) {
        throw new Error("L'ID de boutique est manquant");
      }
      
      // Get the organization ID
      const organizationId = localStorage.getItem('currentOrganizationId');
      
      if (!organizationId) {
        throw new Error("L'ID d'organisation est manquant");
      }
      
      console.log(`[useCreateCustomerForm] Using shop ID: ${preselectedShopId} and organization ID: ${organizationId}`);
      
      if (editCustomerId) {
        // If editing
        const { data: existingCustomer } = await supabase
          .from('customers')
          .select('shop_id, phone')
          .eq('id', editCustomerId)
          .single();
          
        console.log(`[useCreateCustomerForm] Updating customer ${editCustomerId} with shop ID: ${preselectedShopId || existingCustomer?.shop_id || 'None'}`);
        
        // Check if another customer with the same phone exists in this shop (if phone changed)
        if (phone !== existingCustomer?.phone) {
          const { data: phoneCheck } = await supabase
            .from('customers')
            .select('id')
            .eq('phone', phone)
            .eq('shop_id', preselectedShopId || existingCustomer?.shop_id)
            .neq('id', editCustomerId);
            
          if (phoneCheck && phoneCheck.length > 0) {
            setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
            setIsSubmitting(false);
            return;
          }
        }
        
        // Update customer directly with Supabase
        const { data, error } = await supabase
          .from('customers')
          .update({
            name,
            email: email || null,
            phone: phone || null,
            shop_id: preselectedShopId || existingCustomer?.shop_id || null
          })
          .eq('id', editCustomerId)
          .select('*')
          .single();
        
        if (error) {
          if (error.code === '23505') {
            setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
            setIsSubmitting(false);
            return;
          }
          throw error;
        }
        
        if (onClientUpdated && data) {
          onClientUpdated(data);
        }
        
        toast({
          title: 'Client mis à jour',
          description: `${name} a été mis à jour avec succès.`,
        });
        
        onCustomerCreated(editCustomerId, name);
      } else {
        // Check if customer with this phone already exists in this shop
        const { data: phoneCheck } = await supabase
          .from('customers')
          .select('id, name')
          .eq('phone', phone)
          .eq('shop_id', preselectedShopId);
          
        if (phoneCheck && phoneCheck.length > 0) {
          setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
          setIsSubmitting(false);
          return;
        }
        
        // If creating new customer
        console.log(`[useCreateCustomerForm] Creating new customer with shop ID: ${preselectedShopId}`);
        
        // Insert directly with Supabase
        const { data, error } = await supabase
          .from('customers')
          .insert({
            name,
            email: email || null,
            phone: phone || null,
            shop_id: preselectedShopId,
            organization_id: organizationId
          })
          .select('*')
          .single();
        
        if (error) {
          if (error.code === '23505') {
            setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
            setIsSubmitting(false);
            return;
          }
          throw error;
        }
        
        console.log(`[useCreateCustomerForm] Customer created successfully:`, data);
        
        toast({
          title: 'Client créé',
          description: `${name} a été ajouté avec succès.`,
        });
        
        onCustomerCreated(data.id, name);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      
      // Check if it's a unique constraint violation (duplicate phone)
      if (error && typeof error === 'object' && 'code' in error && error.code === '23505') {
        setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
      } else {
        toast({
          title: 'Erreur',
          description: 'Impossible d\'enregistrer le client.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return {
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    errors,
    isSubmitting,
    handleSave,
    loadCustomerData,
  };
};
